import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import icons from "../utils/icons";
import ButtonComponent from "../components/Buttons/button";
import ThemeToggle from "../components/ThemeToggle";
import ColorSchemeToggle from "../components/ColorSchemeToggle/ColorSchemeToggle";
import { isAuthenticated } from "../services/authService";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import { useDispatch, useSelector } from "react-redux";
import {
  getNotifications,
  markAsRead,
} from "../redux/actions/notificationAction";
import MobileNotification from "./mobileNotification";
import { getUserInfo } from "../redux/actions/userActions";
import MyProfileIMG from "../assets/images/profile-img.png";
import Avatar from "react-avatar";

export function HeaderNew({ socket }) {
  const { Logo, VerifyUser, LogoutIcon } = icons();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);

  // Auth-specific state
  const menuRight = useRef(null);
  const menuLeft = useRef(null);
  const notifRef = useRef(null);
  const dispatch = useDispatch();
  const [showMobileNotifications, setShowMobileNotifications] = useState(false);
  const [loading, setLoading] = useState(false);
  const { notifications } = useSelector((state) => state.notifications);
  const { userDetails } = useSelector((state) => state.user);

  const unreadNotificationsCount =
    notifications.length &&
    notifications.filter((item) => item.status === "Unread").length
      ? notifications.filter((item) => item.status === "Unread").length
      : 0;

  useEffect(() => {
    if (isAuthenticated() && socket) {
      socket.on("notificationSent", () => {
        dispatch(getNotifications(setLoading));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket?.connected]);

  useEffect(() => {
    if (isAuthenticated()) {
      dispatch(getUserInfo());
    }
  }, [dispatch]);

  const markNotificationAsRead = (id) => {
    dispatch(markAsRead(id));
  };

  const tradeItems = [
    {
      label: "Private Wallet",
      command: () => navigate("/dashboard"),
    },
    {
      label: "Existing P2P",
      command: () => navigate("/transactions"),
    },
  ];

  const profileItems = [
    {
      label: "My Profile",
      icon: "pi pi-fw pi-user",
      command: () => navigate("/my-profile"),
    },
    {
      label: "Logout",
      icon: <LogoutIcon />,
      command: () => handleLogout(),
    },
  ];

  const notificationItems = [
    { label: "Notifications" },
    {
      template: () => (
        <>
          {notifications.length ? (
            notifications.map((notification, idx) => (
              <div
                key={notification._id || idx}
                className={`${
                  notification.status === "Unread"
                    ? "text-white"
                    : "text-gray-600"
                } text-xs`}
              >
                <ul className="p-2">
                  <Tooltip
                    target=".custom-tooltip-notification"
                    position="left"
                  >
                    mark as read
                  </Tooltip>
                  <li
                    className={`${
                      notification.status === "Unread"
                        ? "custom-tooltip-notification"
                        : ""
                    } mb-1 cursor-pointer shadow-3 hover:shadow-8 border-0 border-round-md p-2`}
                    onClick={() => markNotificationAsRead(notification._id)}
                  >
                    {notification.message}
                    <div className="flex justify-content-end">
                      {moment(notification.createdAt)
                        .startOf("seconds")
                        .fromNow()}
                    </div>
                  </li>
                </ul>
              </div>
            ))
          ) : (
            <div className="text-center text-white">No notifications</div>
          )}
        </>
      ),
    },
  ];

  const handleLogout = () => {
    navigate("/");
    sessionStorage.clear();
    dispatch({ type: "RESET_STATE" });
  };

  // ─── Non-Authenticated Header ─────────────────────────────────────────────
  if (!isAuthenticated()) {
    return (
      <div className="header-new">
        <div className="header-new-container">
          {/* Logo */}
          <NavLink to="/" className="logo-new">
            <Logo className="tinbox-logo" />
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="desktop-nav-new">
            <NavLink to="/register">
              <ButtonComponent
                label="Get Started"
                className="btn-get-started"
                onClick={() => setShowMenu(false)}
              />
            </NavLink>
            <div className="theme-toggle-wrapper">
              <ColorSchemeToggle />
              <ThemeToggle />
            </div>
          </nav>

          {/* Mobile Hamburger */}
          <button
            className="hamburger-new"
            onClick={() => setShowMenu(!showMenu)}
          >
            <i className={`pi ${showMenu ? "pi-times" : "pi-bars"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {showMenu && (
          <div className="mobile-menu-new">
            <NavLink to="/register" onClick={() => setShowMenu(false)}>
              Get Started
            </NavLink>
            <ColorSchemeToggle />
            <ThemeToggle />
          </div>
        )}
      </div>
    );
  }

  // ─── Authenticated Header ────────────────────────────────────────────────
  return (
    <div className="header-new">
      <div className="header-new-container">
        {/* Logo */}
        <NavLink to="/" className="logo-new" onClick={() => setShowMenu(false)}>
          <Logo className="tinbox-logo" />
        </NavLink>

        {/* Desktop Authenticated Nav */}
        <nav className="desktop-nav-new auth-nav-new">
          {/* Trade Dropdown */}
          <div className="trade-dropdown">
            <Menu
              model={tradeItems}
              popup
              ref={menuLeft}
              id="popup_menu_trade"
            />
            <Button
              label="Trade"
              iconPos="right"
              icon="pi pi-angle-down"
              className="nav-text-link auth-nav-btn"
              onClick={(event) => menuLeft.current.toggle(event)}
              aria-controls="popup_menu_trade"
              aria-haspopup
            />
          </div>

          {/* Transaction History */}
          <NavLink
            to="/transactions"
            className={({ isActive }) =>
              `nav-text-link ${isActive ? "active" : ""}`
            }
            onClick={() => setShowMenu(false)}
          >
            Transactions
          </NavLink>

          {/* Notification Bell */}
          <div className="notification-bell">
            <Tooltip target=".notif-bell-icon" />
            <i
              className="notif-bell-icon pi pi-bell p-overlay-badge"
              data-pr-tooltip={
                unreadNotificationsCount > 0
                  ? `${unreadNotificationsCount} unread`
                  : "No notifications"
              }
              data-pr-position="bottom"
              style={{ fontSize: "1.2rem", cursor: "pointer" }}
              onClick={(event) => {
                if (!notifications.length) {
                  dispatch(getNotifications(setLoading));
                }
                notifRef.current.toggle(event);
              }}
            >
              {unreadNotificationsCount > 0 && <Badge severity="danger" />}
            </i>
            <Menu
              model={notificationItems}
              popup
              ref={notifRef}
              id="popup_menu_notif"
              style={{ width: "320px", maxHeight: "500px", overflow: "auto" }}
            />
          </div>

          {/* User Profile Dropdown */}
          <div className="user-profile-new">
            {userDetails?.firstName ? (
              <Avatar
                name={`${userDetails.firstName} ${userDetails.lastName}`}
                round
                size="36"
              />
            ) : (
              <img
                src={MyProfileIMG}
                alt="Profile"
                className="profile-avatar-fallback"
              />
            )}
            <div className="profile-dropdown-section">
              <Menu
                model={profileItems}
                popup
                ref={menuRight}
                id="popup_menu_profile"
              />
              <Button
                label="My Profile"
                iconPos="right"
                icon="pi pi-angle-down"
                className="nav-text-link auth-nav-btn profile-btn"
                onClick={(event) => menuRight.current.toggle(event)}
                aria-controls="popup_menu_profile"
                aria-haspopup
              />
              {userDetails?.isKYCVerified && (
                <div className="kyc-badge">
                  <VerifyUser />
                  <small>Verified</small>
                </div>
              )}
            </div>
          </div>

          {/* Theme Toggle (far right) */}
          <div className="theme-toggle-wrapper">
            <ColorSchemeToggle />
            <ThemeToggle />
          </div>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="hamburger-new"
          onClick={() => {
            setShowMenu(!showMenu);
            setShowMobileNotifications(false);
          }}
        >
          <i className={`pi ${showMenu ? "pi-times" : "pi-bars"}`}></i>
        </button>
      </div>

      {/* Mobile Auth Menu */}
      {showMenu && (
        <div className="mobile-menu-new mobile-auth-menu">
          {showMobileNotifications ? (
            <MobileNotification
              notifications={notifications}
              markNotificationAsRead={markNotificationAsRead}
            />
          ) : (
            <>
              {/* Profile Section */}
              <NavLink
                to="/my-profile"
                onClick={() => setShowMenu(false)}
                className="mobile-profile-section"
              >
                <div className="flex align-items-center gap-2">
                  {userDetails?.firstName ? (
                    <Avatar
                      name={`${userDetails.firstName} ${userDetails.lastName}`}
                      round
                      size="40"
                    />
                  ) : (
                    <img
                      src={MyProfileIMG}
                      alt="Profile"
                      className="profile-avatar-fallback"
                    />
                  )}
                  <div>
                    <h4 className="my-0">My Profile</h4>
                    {userDetails?.isKYCVerified && (
                      <div className="flex align-items-center gap-1">
                        <VerifyUser />
                        <small>Verified User</small>
                      </div>
                    )}
                  </div>
                </div>
              </NavLink>

              {/* Trade Accordion */}
              <div className="mobile-trade-accordion">
                <Accordion activeIndex={0}>
                  <AccordionTab header="Trade">
                    <div className="flex flex-column">
                      <ul className="my-0 list-none pl-0">
                        <li
                          className="py-2 cursor-pointer"
                          onClick={() => {
                            setShowMenu(false);
                            navigate("/dashboard");
                          }}
                        >
                          Private Wallet
                        </li>
                        <li
                          className="py-2 cursor-pointer"
                          onClick={() => {
                            setShowMenu(false);
                            navigate("/transactions");
                          }}
                        >
                          Existing P2P
                        </li>
                      </ul>
                    </div>
                  </AccordionTab>
                </Accordion>
              </div>

              {/* Transaction History */}
              <NavLink
                to="/transactions"
                onClick={() => setShowMenu(false)}
                className={({ isActive }) =>
                  `mobile-nav-link ${isActive ? "active" : ""}`
                }
              >
                Transaction History
              </NavLink>

              {/* Notifications */}
              <a
                className="mobile-nav-link"
                onClick={() => {
                  if (!notifications.length) {
                    dispatch(getNotifications(setLoading));
                  }
                  setShowMobileNotifications(true);
                }}
              >
                Notifications
                {unreadNotificationsCount > 0 && (
                  <Badge
                    value={unreadNotificationsCount}
                    severity="danger"
                    className="ml-2"
                  />
                )}
              </a>

              {/* Theme Toggles */}
              <div className="mobile-theme-toggles">
                <ColorSchemeToggle />
                <ThemeToggle />
              </div>

              {/* Logout */}
              <a
                className="mobile-nav-link logout-link"
                onClick={() => {
                  setShowMenu(false);
                  handleLogout();
                }}
              >
                <LogoutIcon />
                <span>Logout</span>
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}